/**
 * Pure logic for the customer map of listings (services / deals).
 *
 * Mirrors the mobile app's `utils/mapBounds.ts` so both behave the same.
 */
import { subcategoryRefOfListing } from '../../../util/data';

// What one query returns at most. A map can't usefully show more, so past this
// the customer is asked to zoom in instead of being paged through.
export const MAX_MAP_HITS = 100;

export const SAUDI_CENTER = { lat: 24.6366, lng: 46.7168 }; // Riyadh

const EARTH_RADIUS_METERS = 6371000;
const toRadians = deg => (deg * Math.PI) / 180;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toCoordinate = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** A usable pin: both numbers, in range, and not the (0,0) placeholder. */
export const getValidLatLng = geoloc => {
  const lat = toCoordinate(geoloc?.lat);
  const lng = toCoordinate(geoloc?.lng);
  if (lat === null || lng === null) {
    return null;
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  if (lat === 0 && lng === 0) {
    return null;
  }
  return { lat, lng };
};

export const haversineMeters = (origin, dest) => {
  const dLat = toRadians(dest.lat - origin.lat);
  const dLng = toRadians(dest.lng - origin.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(origin.lat)) * Math.cos(toRadians(dest.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

/** "450 m" / "3.2 km". */
export const metersToText = meters =>
  meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;

/**
 * Google map bounds ({north, south, east, west}) -> Algolia `insideBoundingBox`:
 * [[north, east, south, west]].
 */
export const viewportToBoundingBox = ({ north, south, east, west }) => [
  [clamp(north, -90, 90), clamp(east, -180, 180), clamp(south, -90, 90), clamp(west, -180, 180)],
];

const spanOf = bounds => ({
  lat: bounds.north - bounds.south,
  lng: bounds.east - bounds.west,
  centerLat: (bounds.north + bounds.south) / 2,
  centerLng: (bounds.east + bounds.west) / 2,
});

/**
 * Whether the customer has moved far enough from the searched area to search
 * it again: the centre moved by more than `threshold` of the searched
 * span, or the zoom changed by more than `threshold`.
 */
export const hasMovedEnough = (searched, current, threshold = 0.25) => {
  if (!searched || !current) {
    return false;
  }
  const a = spanOf(searched);
  const b = spanOf(current);
  if (!a.lat || !a.lng) {
    return false;
  }
  const latShift = Math.abs(b.centerLat - a.centerLat) / a.lat;
  const lngShift = Math.abs(b.centerLng - a.centerLng) / a.lng;
  const zoomChange = Math.abs(b.lat - a.lat) / a.lat;
  return latShift > threshold || lngShift > threshold || zoomChange > threshold;
};

/** Whether the query matched more listings than were returned. */
export const isResultCapped = (nbHits, shown) => nbHits > shown;

const readAddress = location => {
  if (!location) {
    return '';
  }
  if (typeof location === 'string') {
    return location;
  }
  return location.address || location.city || '';
};

// Algolia hits carry the author flat: the business name is on publicData.
const readMerchantName = author => author?.publicData?.business_name || author?.displayName || '';

/**
 * Algolia service/deal hits -> map listings. Hits without a usable `_geoloc`
 * can't be placed and are dropped.
 *
 * Distance is worked out here from `origin` rather than read from Algolia's
 * `_rankingInfo.geoDistance`: inside a bounding-box search that value is 0 for
 * every hit, which showed everything as "0 m" away.
 */
export const toMapListings = (hits, kind, origin) =>
  (hits || []).reduce((acc, hit) => {
    const pin = getValidLatLng(hit?._geoloc);
    if (!pin || !hit?.objectID) {
      return acc;
    }
    const publicData = hit.publicData || {};
    const amount = Number(hit.price?.amount);
    const hasPrice =
      hit.price?.amount !== null && hit.price?.amount !== undefined && Number.isFinite(amount);
    acc.push({
      id: hit.objectID,
      kind,
      title: hit.title || '',
      image: hit.images?.[0],
      merchantName: readMerchantName(hit.author),
      address: readAddress(publicData.location),
      averageRating: Number(publicData.averageRating) || 0,
      totalReviews: Number(publicData.totalReviews) || 0,
      ...(hasPrice ? { priceAmount: amount, currency: hit.price?.currency } : {}),
      publicData,
      coordinate: pin,
      ...(origin && getValidLatLng(origin)
        ? { distanceMeters: haversineMeters(origin, pin) }
        : {}),
    });
    return acc;
  }, []);

/** One pin per location (to ~1 m), in the order the listings arrived. */
export const groupListingsByLocation = listings => {
  const groups = new Map();
  (listings || []).forEach(listing => {
    const key = `${listing.coordinate.lat.toFixed(5)},${listing.coordinate.lng.toFixed(5)}`;
    const existing = groups.get(key);
    if (existing) {
      existing.listings.push(listing);
    } else {
      groups.set(key, { key, coordinate: listing.coordinate, listings: [listing] });
    }
  });
  return Array.from(groups.values());
};

/**
 * Category tree (as `transformDbCategories` returns it) -> lookup by slug and by
 * id, each entry `{ category, parent }`, for CategoryIcon.
 */
export const buildCategoryIndex = tree => {
  const index = new Map();
  const put = (key, entry) => key && !index.has(String(key)) && index.set(String(key), entry);
  (tree || []).forEach(parent => {
    const parentEntry = { category: parent, parent: null };
    put(parent.slug, parentEntry);
    put(parent.id, parentEntry);
    (parent.childCategory || []).forEach(child => {
      const childEntry = { category: child, parent };
      put(child.slug, childEntry);
      put(child.id, childEntry);
    });
  });
  return index;
};

/**
 * The category to draw for a pin (from its first listing): the subcategory,
 * else the parent. Deals carry their service's subcategory in
 * `listingsCategories`; their own categoryLevel1/2 is the discount type.
 */
export const pinCategory = (group, index) => {
  const listing = group?.listings?.[0];
  if (!listing || !index) {
    return { category: null, parent: null };
  }
  const publicData = listing.publicData || {};
  const sub = subcategoryRefOfListing({
    ...publicData,
    listingType: listing.kind === 'deal' ? 'deal' : publicData.listingType,
  });
  const found =
    (sub && index.get(sub)) ||
    (listing.kind !== 'deal' && publicData.categoryLevel1 && index.get(String(publicData.categoryLevel1)));
  return found || { category: null, parent: null };
};
